import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companiesApi } from '@/lib/companies';
import { ssoApi, SsoConfig, SsoProvider } from '@/lib/sso';
import { AppShell } from '@/components/app/AppShell';
import { AdminSiblingNav } from '@/components/app/AdminSiblingNav';
import { useDialogs } from '@/components/app/DialogProvider';
import { InlineAlert, Modal } from '@/components/app/Modal';
import { Dropdown } from '@/components/ui/Dropdown';

export function SsoAdminPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { confirm } = useDialogs();

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(),
  });
  const companyId = companies[0]?.id;

  const { data: configs = [], isLoading } = useQuery({
    queryKey: ['sso-configs', companyId],
    queryFn: () => ssoApi.list(companyId!),
    enabled: !!companyId,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{
    provider: SsoProvider;
    displayName: string;
    entryPoint: string;
    identifier: string;
    secret: string;
    emailDomain: string;
  }>({
    provider: 'saml',
    displayName: '',
    entryPoint: '',
    identifier: '',
    secret: '',
    emailDomain: '',
  });

  const create = useMutation({
    mutationFn: () => ssoApi.create(companyId!, {
      provider: form.provider,
      displayName: form.displayName,
      entryPoint: form.entryPoint,
      identifier: form.identifier,
      secret: form.secret,
      emailDomain: form.emailDomain || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sso-configs', companyId] });
      setOpen(false);
      setForm({ provider: 'saml', displayName: '', entryPoint: '', identifier: '', secret: '', emailDomain: '' });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => ssoApi.remove(companyId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sso-configs', companyId] }),
  });
  const confirmDelete = async (c: SsoConfig) => {
    const ok = await confirm({
      title: t('sso.delete_confirm', { defaultValue: 'Remove SSO config “{{name}}”? Email-domain routing for it stops immediately.', name: c.displayName }) as string,
      confirmLabel: t('common.remove', { defaultValue: 'Remove' }) as string,
    });
    if (ok) {
      del.mutate(c.id);
    }
  };

  return (
    <AppShell
      title={t('sso.title', { defaultValue: 'Single sign-on' })}
      description={t('sso.subtitle', { defaultValue: 'Authenticate your team through your existing identity provider.' }) as string}
      actions={
        <button onClick={() => setOpen(true)} className="btn-press px-4 py-2 text-sm">
          {t('sso.add_config', { defaultValue: 'Add provider' })}
        </button>
      }
      maxWidth="max-w-5xl"
    >
      <AdminSiblingNav wsId={wsId!} current="sso" />
      <div className="space-y-5">
        <div className="card-paper px-4 py-3 text-sm text-ink-700">
          <span className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold">
            {t('sso.metadata_link')}
          </span>
          <div className="mt-1">
            <a
              href={ssoApi.metadataUrl()}
              target="_blank"
              rel="noreferrer noopener"
              className="num text-accent-600 hover:underline break-all"
            >
              {ssoApi.metadataUrl()}
            </a>
          </div>
        </div>

        {isLoading ? (
          <div className="card-paper p-10 text-center text-ink-500 text-sm">{t('common.loading')}</div>
        ) : configs.length === 0 ? (
          <div className="card-paper p-12 text-center text-ink-500 text-sm">
            {t('sso.empty', { defaultValue: 'No SSO providers configured yet.' })}
          </div>
        ) : (
          <ul className="space-y-3">
            {configs.map((c) => <ConfigRow key={c.id} config={c} t={t} onDelete={confirmDelete} />)}
          </ul>
        )}
      </div>

      <Modal
        open={open}
        onOpenChange={(next) => { setOpen(next); if (!next) create.reset(); }}
        title={t('sso.add_config', { defaultValue: 'Add provider' })}
        description={t('sso.add_help', { defaultValue: 'Paste the values your IdP wizard returned. We encrypt the secret at rest.' }) as string}
        size="lg"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('sso.provider')}>
            <div className="w-full">
              <Dropdown<SsoProvider>
                value={form.provider}
                onChange={(next) => setForm((f) => ({ ...f, provider: next }))}
                items={[
                  { value: 'saml', label: t('sso.provider_saml') as string },
                  { value: 'oidc', label: t('sso.provider_oidc') as string },
                ]}
                ariaLabel={t('sso.provider') as string}
                size="sm"
                className="w-full"
              />
            </div>
          </Field>
          <Field label={t('sso.display_name')}>
            <Input value={form.displayName} onChange={(v) => setForm((f) => ({ ...f, displayName: v }))} />
          </Field>
          <Field label={t('sso.entry_point')} className="md:col-span-2">
            <Input
              value={form.entryPoint}
              onChange={(v) => setForm((f) => ({ ...f, entryPoint: v }))}
              placeholder="https://acme.okta.com/app/.../sso/saml"
              mono
            />
          </Field>
          <Field label={t('sso.identifier')}>
            <Input value={form.identifier} onChange={(v) => setForm((f) => ({ ...f, identifier: v }))} mono />
          </Field>
          <Field label={t('sso.email_domain')}>
            <Input value={form.emailDomain} onChange={(v) => setForm((f) => ({ ...f, emailDomain: v }))} placeholder="acme.com" mono />
          </Field>
          <Field label={t('sso.secret')} className="md:col-span-2">
            <textarea
              value={form.secret}
              onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
              rows={4}
              placeholder="-----BEGIN CERTIFICATE-----"
              className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            />
          </Field>
        </div>
        {create.error && (
          <div className="mt-3">
            <InlineAlert tone="error">{(create.error as Error).message}</InlineAlert>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-sm">
            {t('common.cancel')}
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={create.isPending || !form.displayName || !form.entryPoint || !form.identifier || !form.secret}
            className="btn-press px-4 py-2 text-sm"
          >
            {create.isPending ? t('sso.saving') : t('sso.save')}
          </button>
        </div>
      </Modal>
    </AppShell>
  );
}

function ConfigRow({ config, t, onDelete }: { config: SsoConfig; t: (k: string) => string; onDelete: (c: SsoConfig) => void }) {
  return (
    <li className="card-paper p-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="display text-[15px] font-semibold text-ink-900">{config.displayName}</h3>
          <p className="text-[12px] text-ink-500 num mt-0.5">{config.provider} · {config.identifier}</p>
          {config.emailDomain && <p className="text-[12px] text-ink-500 num">↳ {config.emailDomain}</p>}
        </div>
        <div className="flex items-center gap-3">
          <a href={ssoApi.loginUrl(config.id)} className="text-[12px] text-accent-600 hover:underline">
            {t('sso.test_login')} ↗
          </a>
          <button onClick={() => onDelete(config)} className="text-[12px] text-rose-600 hover:text-rose-700">
            {t('common.delete')}
          </button>
        </div>
      </div>
    </li>
  );
}

function Field({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-[11px] uppercase tracking-[0.12em] text-ink-500 mb-1 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function Input({
  value, onChange, placeholder, mono,
}: { value: string; onChange: (next: string) => void; placeholder?: string; mono?: boolean }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30 ${mono ? 'num' : ''}`}
    />
  );
}
